import React, { type FC, useCallback, useState } from "react";
import { EmailInput, validateEmail } from "@components/Auth/Form/Email";
import {
  trackError,
  trackStarted,
  trackSuccess,
  withPrefix,
} from "@tracking/tracking";
import { useLandingPagePath } from "clientState/AuthFlow/hooks";
import type { OnSuccessType } from "clientState/AuthFlow/hooks/useForgotPassword/contract";
import { useForgotPassword } from "clientState/AuthFlow/hooks/useForgotPassword/useForgotPassword";
import { useModalActions } from "clientState/AuthFlow/providers/ModalScreenProvider";
import {
  screenNames,
  useScreenState,
} from "clientState/AuthFlow/providers/ScreenProvider";
import { useTracking, useTranslation } from "@edreamsodigeo/frontend-core";
import { Flex } from "@edreamsodigeo/prisma-design-system";

import ModalMessage from "../Helpers/ModalMessage";
import SubmitButton, { submitButtonId } from "../Passwordless/SubmitButton";
import { type ForgotPasswordBodyProps } from "./contract";

const { FORGOT_PASSWORD } = screenNames;
export const CLICKED_ID = `${FORGOT_PASSWORD}_click`;
const screen = FORGOT_PASSWORD;

const ForgotPasswordBody: FC<ForgotPasswordBodyProps> = ({
  submitButtonLabel,
  emailLabelForm,
  trackingPrefix,
  renderSuccessScreen,
}) => {
  const { updateAuthenticationStatus } = useModalActions();
  const { trackEvent } = useTracking();
  const { state } = useScreenState();

  const { t } = useTranslation();
  const [email, setEmail] = useState(state.userData.email);
  const [emailErrors, setEmailErrors] = useState<string[]>([]);
  const [showError, setShowError] = useState(false);
  const [loading, setLoading] = useState(false);
  const landingPagePath = useLandingPagePath();

  const [acceptsNewsletter] = useState(state.userData.acceptsNewsletter);

  const successAuthEmail = useCallback<OnSuccessType>(
    ({ email, acknowledged }) => {
      setLoading(false);
      if (acknowledged) {
        trackSuccess({
          trackEvent,
          screen,
          prefix: trackingPrefix,
        });
        renderSuccessScreen({ email });
        updateAuthenticationStatus("email-sent");
      } else {
        trackError({
          trackEvent,
          screen,
          prefix: trackingPrefix,
        });
        setShowError(true);
      }
    },
    [
      renderSuccessScreen,
      trackingPrefix,
      trackEvent,
      updateAuthenticationStatus,
    ],
  );

  const errorAuthEmail = useCallback(() => {
    trackError({
      trackEvent,
      screen,
      prefix: trackingPrefix,
    });
    setShowError(true);
    setLoading(false);
  }, [trackingPrefix, trackEvent]);

  const onEnterSubmit = (): void => {
    document.getElementById(submitButtonId)?.click();
  };

  const { sendSetPasswordEmail } = useForgotPassword();

  const handleSubmit = useCallback(async () => {
    setShowError(false);
    setLoading(true);
    trackStarted({ trackEvent, screen, prefix: trackingPrefix });
    const { value: isValidEmail, errors: emailErrors } = validateEmail(email);

    if (isValidEmail) {
      sendSetPasswordEmail({
        email,
        landingPagePath,
        newsletterSubscribed: acceptsNewsletter,
        onSuccess: successAuthEmail,
        onError: errorAuthEmail,
      });
    } else {
      setEmailErrors(emailErrors);
      trackError({ trackEvent, screen, prefix: trackingPrefix });
      setLoading(false);
    }
  }, [
    trackEvent,
    trackingPrefix,
    email,
    sendSetPasswordEmail,
    landingPagePath,
    acceptsNewsletter,
    successAuthEmail,
    errorAuthEmail,
  ]);

  return (
    <>
      <Flex flexDirection="column" gap="1x_small" my="base">
        {showError && (
          <ModalMessage
            type="ERROR"
            message={t("authentication.forgotPassword.error")}
          />
        )}
        <EmailInput
          id="id-email-input"
          email={email}
          setEmail={setEmail}
          errors={emailErrors}
          clearErrors={(): void => setEmailErrors([])}
          disabled={loading}
          label={emailLabelForm}
          onEnter={onEnterSubmit}
          trackingPrefix={trackingPrefix}
        />
      </Flex>
      <Flex flexDirection="column" gap="base">
        <SubmitButton
          loading={loading}
          onClick={handleSubmit}
          label={submitButtonLabel}
          trackingId={withPrefix(CLICKED_ID, trackingPrefix)}
        />
      </Flex>
    </>
  );
};

export default ForgotPasswordBody;
